import { Icon } from "@legal-500/legal500-design-system";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./LanguageTabBar.module.css";

export type LanguageTab = {
  code: string;
  label: string;
};

export type LanguageTabBarProps = {
  languages: LanguageTab[];
  activeCode: string;
  onChange: (code: string) => void;
  /** Languages the user MAY add (not already in `languages`). When provided, the
   *  "+ Add language" button opens a dropdown of these. */
  availableLanguages?: LanguageTab[];
  /** Fired with the chosen code from the dropdown. */
  onAdd?: (code: string) => void;
  onRemove?: (code: string) => void;
  addLabel?: string;
};

function sortAvailable(langs: LanguageTab[]): LanguageTab[] {
  return [...langs].sort((a, b) => {
    if (a.code === "en") return -1;
    if (b.code === "en") return 1;
    return a.label.localeCompare(b.label);
  });
}

export function LanguageTabBar({
  languages,
  activeCode,
  onChange,
  availableLanguages,
  onAdd,
  onRemove,
  addLabel = "Add language",
}: LanguageTabBarProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const sortedAvailable = useMemo(
    () => sortAvailable(availableLanguages ?? []),
    [availableLanguages],
  );

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleAdd = (code: string) => {
    onAdd?.(code);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const showAddButton = !!onAdd;
  const hasAvailable = sortedAvailable.length > 0;

  return (
    <div className={styles.bar} role="tablist" aria-label="Languages">
      {languages.map(lang => {
        const isActive = lang.code === activeCode;
        return (
          <div key={lang.code} className={clsx(styles.tab, isActive && styles.active)}>
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`language-panel-${lang.code}`}
              id={`language-tab-${lang.code}`}
              className={styles.tabButton}
              onClick={() => onChange(lang.code)}
            >
              {lang.label}
            </button>
            {onRemove && (
              <button
                type="button"
                className={styles.removeButton}
                aria-label={`Remove ${lang.label}`}
                onClick={e => {
                  e.stopPropagation();
                  onRemove(lang.code);
                }}
              >
                <Icon icon="cross" size="small" />
              </button>
            )}
          </div>
        );
      })}
      {showAddButton && (
        <div className={styles.addWrapper} ref={wrapperRef}>
          <button
            type="button"
            ref={triggerRef}
            className={styles.addButton}
            onClick={() => setOpen(v => !v)}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={addLabel}
            disabled={!hasAvailable}
          >
            <Icon icon="plus" size="small" />
            <span>{addLabel}</span>
          </button>
          {open && hasAvailable && (
            <div className={styles.dropdown} role="menu">
              {sortedAvailable.map(lang => (
                <button
                  key={lang.code}
                  type="button"
                  role="menuitem"
                  className={styles.dropdownItem}
                  onClick={() => handleAdd(lang.code)}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
